import { useState, useEffect, useMemo } from 'react';
import {
  Loader2,
  AlertCircle,
  ScrollText,
  ChevronDown,
  ChevronRight,
  GitCompare,
  Eye,
} from 'lucide-react';
import { AppLayout } from './AppLayout';
import { TextComparisonDialog, ComparisonItem } from './TextComparisonDialog';
import { SystemPromptViewerDialog } from './SystemPromptViewerDialog';
import { ApiService } from '../services/apiService';
import { SystemPrompt, SystemPromptScoreEntry } from '../types/systemPrompt';

type StatusFilter = 'active' | 'inactive';

export const SystemPromptsPage = () => {
  const apiService = ApiService.getInstance();

  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [selectedHashes, setSelectedHashes] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [viewingPrompt, setViewingPrompt] = useState<{ prompt: SystemPrompt; versionLabel: string } | null>(null);
  const [scores, setScores] = useState<Record<string, SystemPromptScoreEntry>>({});

  const loadPrompts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getSystemPromptsWithContent();
      setPrompts(data);
    } catch (err: any) {
      console.error('Failed to load system prompts:', err);
      setError(err.message || 'Failed to load system prompts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
    apiService.getSystemPromptScores().then(data => {
      console.log('[SystemPromptsPage] scores loaded:', data);
      setScores(data);
    }).catch(err => console.error('[SystemPromptsPage] scores failed:', err));
  }, []);

  const filteredPrompts = useMemo(() => {
    return prompts.filter(p =>
      statusFilter === 'active' ? p.isActive : !p.isActive
    );
  }, [prompts, statusFilter]);

  const groupedPrompts = useMemo(() => {
    const groups: Record<string, SystemPrompt[]> = {};
    for (const p of filteredPrompts) {
      const key = p.agentType || 'Unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    // Sort groups alphabetically
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredPrompts]);

  const toggleSelection = (hash: string) => {
    setSelectedHashes(prev => {
      const next = new Set(prev);
      if (next.has(hash)) {
        next.delete(hash);
      } else {
        next.add(hash);
      }
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  const comparisonItems: ComparisonItem[] = useMemo(() => {
    return Array.from(selectedHashes).map(hash => {
      const p = prompts.find(pr => pr.hash === hash)!;
      return {
        id: p.hash,
        label: p.description,
        content: p.content,
        createdAt: p.createdAt,
      };
    });
  }, [selectedHashes, prompts]);

  const handleCompare = () => {
    if (selectedHashes.size === 2) {
      setShowComparison(true);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const daysAgo = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 86_400_000);
    return diff === 0 ? 'today' : diff === 1 ? '1 day ago' : `${diff} days ago`;
  };

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <AppLayout pageTitle="Prompts" pageDescription="System prompt versions by agent type">
      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          {/* Status Filter */}
          <div data-flow="status-filter" className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {([
              { value: 'active' as StatusFilter, label: 'Active' },
              { value: 'inactive' as StatusFilter, label: 'Inactive' },
            ]).map(({ value, label }) => (
              <button
                key={value}
                onClick={() => {
                  setStatusFilter(value);
                  setSelectedHashes(new Set());
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Compare Button */}
          <button
            onClick={handleCompare}
            disabled={selectedHashes.size !== 2}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <GitCompare className="w-4 h-4" />
            <span className="text-sm font-medium">Compare Selected</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div data-component="SystemPromptsPage" className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div data-flow="loading" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-gray-500">Loading system prompts...</p>
            </div>
          </div>
        ) : error ? (
          <div data-flow="error" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3 text-red-600">
              <AlertCircle className="w-12 h-12" />
              <p className="font-medium">{error}</p>
              <button
                onClick={loadPrompts}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : groupedPrompts.length === 0 ? (
          <div data-flow="empty" className="flex flex-col items-center justify-center h-64 text-gray-500">
            <ScrollText className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No {statusFilter} prompts found</p>
            <p className="text-sm mt-2">Try switching the filter above</p>
          </div>
        ) : (
          <div data-flow="results" className="max-w-7xl mx-auto space-y-4">
            {groupedPrompts.map(([agentType, groupPrompts]) => {
              const isCollapsed = collapsedGroups.has(agentType);
              return (
                <div key={agentType} className="bg-white rounded-lg border border-gray-200">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(agentType)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{capitalize(agentType)}</span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          {groupPrompts.length} {groupPrompts.length === 1 ? 'version' : 'versions'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{groupPrompts[0].purpose}</p>
                    </div>
                  </button>

                  {/* Prompt Cards */}
                  {!isCollapsed && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                      {groupPrompts.map((prompt, idx) => {
                        const versionNum = groupPrompts.length - idx;
                        const isLatest = idx === 0;
                        const contentPreview = prompt.content
                          ? prompt.content.split('\n').filter(l => l.trim()).slice(0, 2).join(' ').slice(0, 120)
                          : '';
                        const charCount = prompt.content?.length ?? 0;
                        const lineCount = prompt.content?.split('\n').length ?? 0;

                        return (
                          <div
                            key={prompt.hash}
                            className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                          >
                            {/* Checkbox */}
                            <input
                              type="checkbox"
                              checked={selectedHashes.has(prompt.hash)}
                              onChange={() => toggleSelection(prompt.hash)}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900">
                                  v{versionNum} - {prompt.description}
                                </span>
                                {isLatest && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800">
                                    Latest
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    prompt.isActive
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {prompt.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              {contentPreview && (
                                <p className="text-sm text-gray-500 line-clamp-1 mb-1 italic">
                                  {contentPreview}{contentPreview.length >= 120 ? '…' : ''}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-gray-400">
                                <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                  {prompt.hash.slice(0, 8)}
                                </code>
                                <span>{formatDate(prompt.createdAt)} ({daysAgo(prompt.createdAt)})</span>
                                <span>{lineCount} lines · {charCount.toLocaleString()} chars</span>
                              </div>
                            </div>

                            {/* Score + View */}
                            <div className="mt-1 flex items-center gap-2 flex-shrink-0">
                              {scores[prompt.hash]?.userScore != null && (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                                  U: {scores[prompt.hash].userScore!.toFixed(1)}
                                </span>
                              )}
                              {scores[prompt.hash]?.aiScore != null && (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                                  AI: {scores[prompt.hash].aiScore!.toFixed(1)}
                                </span>
                              )}
                              {scores[prompt.hash]?.aiBreakdown && (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-purple-50 text-purple-700" title={`Accuracy: ${Math.round(scores[prompt.hash].aiBreakdown!.accuracy * 100)}% | Citations: ${Math.round(scores[prompt.hash].aiBreakdown!.citationQuality * 100)}% | Complete: ${Math.round(scores[prompt.hash].aiBreakdown!.completeness * 100)}% | Legal: ${Math.round(scores[prompt.hash].aiBreakdown!.legalQuality * 100)}%`}>
                                  A:{Math.round(scores[prompt.hash].aiBreakdown!.accuracy * 100)}% C:{Math.round(scores[prompt.hash].aiBreakdown!.citationQuality * 100)}% Co:{Math.round(scores[prompt.hash].aiBreakdown!.completeness * 100)}% L:{Math.round(scores[prompt.hash].aiBreakdown!.legalQuality * 100)}%
                                </span>
                              )}
                              <button
                                onClick={() => setViewingPrompt({ prompt, versionLabel: `v${versionNum}` })}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comparison Dialog */}
      {showComparison && (
        <TextComparisonDialog
          isOpen={showComparison}
          title="Compare System Prompts"
          items={comparisonItems}
          initialLeftId={comparisonItems[0]?.id}
          initialRightId={comparisonItems[1]?.id}
          hideApprove={true}
          onApprove={() => {}}
          onClose={() => setShowComparison(false)}
        />
      )}

      {/* Viewer Dialog */}
      {viewingPrompt && (
        <SystemPromptViewerDialog
          prompt={viewingPrompt.prompt}
          versionLabel={viewingPrompt.versionLabel}
          scoreEntry={scores[viewingPrompt.prompt.hash] ?? null}
          onClose={() => setViewingPrompt(null)}
        />
      )}
    </AppLayout>
  );
};
